import cloneDeep from 'lodash-es/cloneDeep';
import {ReactiveController, ReactiveControllerHost} from 'lit';
import {SearchObject} from '../models/SearchObject';
import {log} from './LogController';
import {FeatureCollection} from 'geojson';
import {ConfigurationState} from '../storage/ConfigurationState';
import {LatLng} from 'leaflet';

export interface IControllerDictionary {
  [controllerId: 'default' | string]: SearchController;
}

type IUrlType = 'geoJson' | 'json';

export type IUrls = {
  [index in IUrlType]?: string;
};

export class SearchController implements ReactiveController {
  private static _instances: Partial<IControllerDictionary> = {};
  searchObject = new SearchObject();
  searchWasManuallyTriggered: boolean = false;

  private _urls: IUrls = {};
  private _abortControllers: {
    [index in IUrlType]?: AbortController;
  } = {};
  private _hosts: ReactiveControllerHost[] = [];
  private _initialSearchObject?: SearchObject;

  private _data: {
    [index in IUrlType]?: FeatureCollection;
  } = {};

  public get data() {
    return this._data;
  }

  static get urls(): IUrls {
    return this._instances['default']?._urls || {};
  }

  static instance(
    host: ReactiveControllerHost,
    controllerId: string = 'default',
    urls?: IUrls,
    searchObject?: SearchObject,
  ) {
    log(this.name, host, controllerId, urls, searchObject);
    if (this._instances[controllerId] === undefined) {
      this._instances[controllerId] = new SearchController();
    }

    const controller = <SearchController>this._instances[controllerId];
    controller._hosts.push(host);

    if (searchObject) {
      // controller._initialSearchObject = cloneDeep(searchObject);
      controller.searchObject = searchObject;
      log(this.name, 'searchObject:', searchObject);
    }

    let doFetch = false;
    if (urls) {
      doFetch = true;
      controller._urls = urls;
    }

    host.addController(controller);

    if (doFetch) {
      controller.fetch();
    }

    return controller;
  }

  updated() {
    this._hosts.forEach(
      h => h.requestUpdate(),
    );
  }

  hostConnected() {
    // should be used for listeners etc. Shadow DOM is ready.
  }

  fetch(urlType?: IUrlType) {
    if (urlType === undefined) {
      Object.keys(this._urls).forEach(
        (type) => {
          this.fetch(type as IUrlType);
        },
      );
      return;
    }

    if (this._abortControllers[urlType]) {
      // log(this.constructor.name, 'aborting', urlType, this._abortControllers[urlType]!.signal.aborted);
      (<AbortController>this._abortControllers[urlType]).abort();
    }
    this._abortControllers[urlType] = new AbortController();

    this._data[urlType] = undefined;
    this.updated();

    const url = new URL(<string>this._urls[urlType]);
    if (urlType === 'geoJson') {
      url.search = this.obj2params(this.searchUriParams());
    } else {
      url.search = this.obj2params(this.countUriParams());
    }

    log(this.constructor.name, 'fetching', urlType, url.toString());
    fetch(
      url,
      {
        // headers: {
        //   'Content-Type': 'application/x-www-form-urlencoded',
        // },
        signal: (<AbortController>this._abortControllers[urlType]).signal,
      },
    ).then(
      response => {
        log(this.constructor.name, 'response', response);
        if (!response.ok) {
          throw new Error(`Failed to fetch "${url}"`);
        }
        return response.json();
      },
    ).then(
      data => {
        log(this.constructor.name, 'fetch completed', urlType, data);
        this._data[urlType] = data;
        this.updated();
      },
    ).catch(
      err => {
        if (err.name !== 'AbortError') {
          throw err;
        }
      },
    );
    log(this.constructor.name, ' after fetch()');
  }

  reset() {
    if (this._initialSearchObject !== undefined) {
      this.searchObject = cloneDeep(this._initialSearchObject);
      this.searchObject.locationStr = this.searchObject.lat = this.searchObject.lng = this.searchObject.radiusKm = '';
      this.searchObject.fullText = '';
      log(this.constructor.name, 'controller::reset()', 'after', this.searchObject);
      this.fetch();
    }
  }

  obj2params(obj: Object, prefix?: string): string {
    let str = [];
    for (const p in obj) {
      if (obj.hasOwnProperty(p)) {
        // @ts-ignore
        let k = prefix ? prefix + '[' + p + ']' : p, v = obj[p];
        str.push(v !== null && typeof v === 'object'
          ? this.obj2params(v, k)
          : encodeURIComponent(k) + '=' + encodeURIComponent(v));
      }
    }
    return str.join('&');
  }

  countUriParams() {
    let _params = {
      log: {
        siteName: ConfigurationState.configuration.siteName,
        action: 'count',
      },
      rules: {
        condition: 'AND',
        rules: [
          {
            field: 'groups',
            operator: 'any',
            value: ConfigurationState.configuration.groups,
          },
          {
            field: 'active',
            operator: 'equals',
            value: 1,
          },
          {
            field: 'geoLat',
            operator: 'unequals',
            value: 0,
          },
          {
            field: 'crawlingOk',
            operator: 'equals',
            value: 1,
          },
        ],
      },
      actions: ['dropData'],
    };

    _params.rules.rules.push(
      {
        field: 'geoLat',
        operator: 'unequals',
        value: 0,
      },
    );

    return _params;
  }

  searchUriParams() {
    let _params = {
      log: {
        siteName: ConfigurationState.configuration.siteName,
        action: 'list',
        location: this.searchObject.locationStr,
      },
      rules: this.searchParamsRules(),
      additionalFields: [
        'sites.geo_lat',
        'sites.geo_lng',
        'sites.site_url',
        'sites.company',
        'sites.title',
        'sites.description',
        'sites.address',
        'sites.zip',
        'sites.city',
        'sites.slug',
        'GROUP_CONCAT(DISTINCT tags.name SEPARATOR \' | \') AS tagNames',
        'CONCAT(LEFT(sites.description,300),SUBSTRING_INDEX(SUBSTRING(sites.description,301),\' \',1),IF(CHAR_LENGTH(sites.description)>300,\'…\',\'\')) AS description',
        'IF(image IS NULL, NULL, 1) AS hasImage',
        'sites.screenshot_path',
        'sites.favicon_path',
        'sm_twitter.profile_url AS social_media_url_twitter',
        'sm_facebook.profile_url AS social_media_url_facebook',
        'sm_linkedin.profile_url AS social_media_url_linkedin',
        'sm_xing.profile_url AS social_media_url_xing',
        'sm_instagram.profile_url AS social_media_url_instagram',
        'sm_tiktok.profile_url AS social_media_url_tiktok',
        'sm_youtube.profile_url AS social_media_url_youtube',
      ],
      actions: {
        maxFulltextMarkedHits: 3,
      },
    };

    _params.rules.rules.push(
      {
        field: 'geoLat',
        operator: 'unequals',
        value: 0,
      },
    );

    return _params;
  }

  searchParamsRules() {
    let rules: any[] = [];

    rules.push(
      {
        field: 'fulltextSplit',
        operator: 'contains',
        value: this.searchObject.fullText,
      },
    );

    // limit to crawlable sites
    rules.push(
      {
        field: 'crawlingOk',
        operator: 'equals',
        value: 1,
      },
    );

    // always add groups constraint
    rules.push(
      {
        field: 'groups',
        operator: 'any',
        value: ConfigurationState.configuration.groups,
      },
    );

    // always add "active" constraint
    rules.push(
      {
        field: 'active',
        operator: 'equals',
        value: 1,
      },
    );

    return {
      condition: 'AND',
      rules: rules,
    };
  }

  distance(latlng1: LatLng, latlng2: LatLng): number {
    const R = 6371000,
      rad = Math.PI / 180,
      lat1 = latlng1.lat * rad,
      lat2 = latlng2.lat * rad,
      sinDLat = Math.sin((latlng2.lat - latlng1.lat) * rad / 2),
      sinDLon = Math.sin((latlng2.lng - latlng1.lng) * rad / 2),
      a = sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLon * sinDLon,
      c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }
}
